import {
  Component,
  ElementRef,
  HostListener,
  Renderer2,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { KeycloakService } from "keycloak-angular";
import { KeycloakProfile } from "keycloak-js";
import { AuthService } from "../../services/auth/auth.service";
import { MenuSections } from "../../shared/utils/menu-tree";
import {
  MenuSectionType,
  MenuItemTreeType,
  MenuTreeType,
} from "../../types/menu-tree.type";
import { Role } from "../../shared/utils/role.model";
import { TranslateService } from "@ngx-translate/core";
import { filter, Subscription } from "rxjs";
import { CompanyApiService } from "../../services/generated/waba-api/services/company-api.service";
import { ConversationApiService } from "../../services/generated/waba-api/services/conversation-api.service";
import { SidebarPanelService } from "../../services/sidebar/sidebar-panel.service";
import { v4 as uuidv4 } from "uuid";
import { APP_PATHS } from "../../shared/utils/app-route-paths";
import { SILENT_HTTP_CONTEXT } from "../../shared/utils/silent-http.util";
import { AccessManagementService } from "../../services/access/access-management.service";
import { CompanyTrialStatusApiService } from "../../services/company/company-trial-status-api.service";
import { SetupChecklistService } from "../../features/onboarding/services/setup-checklist.service";
import { NARROW_RESOURCE_ROLE_CODES } from "../../shared/utils/business-permission-catalog";
import { RestaurantWordsService } from "../../services/restaurant-words/restaurant-words.service";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
})
export class SidebarComponent implements OnInit, OnDestroy {
  protected readonly allSections = MenuSections;
  visibleSections: MenuSectionType[] = [];

  // Sidebar state.
  // Sub-menus mirror the collapsed rail flyout: every group is open, and we
  // only remember the ones the user deliberately folded away.
  collapsedMenus = new Set<string>();
  collapsedMobileSections = new Set<string>();
  expandedMobileSections = new Set<string>();
  // Rail clicks navigate but should keep the panel open so the user can browse
  // the section; every other navigation collapses an unpinned panel.
  private suppressNextAutoCollapse = false;

  // Flyout menu shown next to the rail while the panel is collapsed
  flyoutSection: string | null = null;
  flyoutStyle: Record<string, string> = {};

  profile!: KeycloakProfile | null;
  userGroups: string[] = [];
  companyActivity: string | undefined;
  isTrialReadOnly = false;
  displayGroupAgent = false;
  agentGroupName: string | null = null;
  private routerSubscription?: Subscription;

  get profilePhone(): string | null {
    const profile = this.profile as (KeycloakProfile & {
      mobile?: string;
      phoneNumber?: string;
      attributes?: Record<string, string | string[]>;
    }) | null;
    const attribute = profile?.attributes?.["mobile"] ?? profile?.attributes?.["phoneNumber"];
    const value = Array.isArray(attribute) ? attribute[0] : attribute;
    return profile?.mobile || profile?.phoneNumber || value || null;
  }

  // Unread conversations badge
  unreadConversations = 0;
  private unreadPollingInterval: any = null;

  constructor(
    private authService: AuthService,
    private keycloakService: KeycloakService,
    private translate: TranslateService,
    private renderer: Renderer2,
    private el: ElementRef,
    private router: Router,
    private companyApiService: CompanyApiService,
    private conversationApiService: ConversationApiService,
    public sidebarPanel: SidebarPanelService,
    private accessManagementService: AccessManagementService,
    private trialStatusApi: CompanyTrialStatusApiService,
    private checklist: SetupChecklistService,
    private restaurantWords: RestaurantWordsService,
  ) {
    this.translate.onLangChange.subscribe((event) => {
      this.checkLanguageDirection(event.lang);
    });
  }

  async ngOnInit() {
    await this.loadUserInfo();
    this.checkLanguageDirection();
    this.restoreSidebarState();

    // A trial-downgraded admin has zero Keycloak groups (see
    // CompanyService#downgradeToReadOnlyIfTrialExpired — group role-mappings would otherwise
    // re-grant the manage roles the downgrade just removed), so the ordinary groups+authorities
    // AND gate below would hide every section entirely rather than showing it read-only. Once
    // trial status confirms this account is genuinely expired (same source of truth the banner
    // already trusts), section/item visibility falls back to authorities alone — which still
    // correctly hides anything requiring a manage-only role with no *_VIEW equivalent.
    this.trialStatusApi.getTrialStatus().subscribe({
      next: (status) => {
        this.isTrialReadOnly = !!status?.endsAt && !status.active;
        this.buildVisibleSections();
      },
      error: () => {
        this.isTrialReadOnly = false;
      },
    });

    // The onboarding rail entry only earns its place once there is real work left
    // in it — see hasOnboardingWork(). Refresh the data-driven steps (meta /
    // messaging / templates) once here so that check has real data on first load,
    // not just whatever a previous visit to the checklist page happened to cache.
    this.checklist.refresh(() => this.buildVisibleSections());

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects || event.url;
        this.setActiveSectionFromRoute(url);
        this.closeDrawer();
        this.closeFlyout();
        this.autoCollapseAfterNavigation();
      });

    this.setActiveSectionFromRoute(this.router.url);

    // Unread inbox badge polling — messaging group + TEAM_INBOX only.
    if (this.canPollUnreadConversations()) {
      this.fetchUnreadCount();
      this.unreadPollingInterval = setInterval(
        () => this.fetchUnreadCount(),
        30000,
      );
    }
  }

  ngOnDestroy() {
    if (this.unreadPollingInterval) {
      clearInterval(this.unreadPollingInterval);
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  // ---- Data Loading ----

  async loadUserInfo() {
    this.profile = await this.authService.getProfile();
    await this.loadUserGroups();
    this.buildVisibleSections();
    this.loadCompanyActivity();
    this.accessManagementService.getCurrentGroup().subscribe({
      next: (response) => (this.agentGroupName = response.body || null),
      error: () => (this.agentGroupName = null),
    });
  }

  loadCompanyActivity() {
    this.companyApiService
      .getCompanyByCompanyId(
        {
          "x-api-requestId": uuidv4(),
          "x-api-canal": "web",
        },
        SILENT_HTTP_CONTEXT,
      )
      .subscribe({
        next: (response) => {
          this.companyActivity = response.body?.activity;
          this.displayGroupAgent = response.body?.displayGroupAgent === true;
          this.restaurantWords.setActivity(this.companyActivity);
          this.buildVisibleSections();
        },
        error: () => {
          this.companyActivity = undefined;
          this.displayGroupAgent = false;
          this.restaurantWords.setActivity(undefined);
          this.buildVisibleSections();
        },
      });
  }

  fetchUnreadCount() {
    if (!this.canPollUnreadConversations()) {
      this.unreadConversations = 0;
      return;
    }

    this.companyApiService
      .companyNumbersList(
        {
          "x-api-requestId": uuidv4(),
          "x-api-canal": "web",
        },
        SILENT_HTTP_CONTEXT,
      )
      .subscribe({
        next: (response) => {
          const phones = response.body || [];
          if (phones.length > 0 && phones[0].reference) {
            this.conversationApiService
              .listConversationsOfCompanyNumber({
                "x-api-requestId": uuidv4(),
                "x-api-canal": "web",
                refPhoneCompany: phones[0].reference,
                body: {
                  filter: { filterEnum: "UNREAD" },
                  page: 0,
                  size: 1,
                },
              })
              .subscribe({
                next: (convResponse) => {
                  this.unreadConversations =
                    convResponse.body?.totalElements || 0;
                },
              });
          }
        },
      });
  }

  async loadUserGroups() {
    try {
      this.userGroups = await this.authService.getUserGroups();
    } catch (error) {
      console.warn("Could not load user groups:", error);
      this.userGroups = [];
    }
  }

  private hasUserGroup(requiredGroups: string[] | undefined): boolean {
    if (!requiredGroups || requiredGroups.length === 0) {
      return true;
    }
    return this.authService.hasAnyGroup(requiredGroups);
  }

  /**
   * True when the account directly holds one of this item's narrow,
   * single-resource roles (CONTACT_VIEW, PRODUCT_VIEW, ...) rather than
   * inheriting access via a Keycloak group. An agent given exactly one
   * resource's role through the fine-grained access picker never joins a
   * group for it (joining would also grant that group's whole bundle),
   * so without this the group gate hid every menu item for such an agent
   * even though they hold the exact authority the item requires.
   */
  private hasDirectResourceAccess(menu: MenuTreeType): boolean {
    if (!menu.authorities?.length) {
      return false;
    }
    return menu.authorities.some(
      (role) =>
        NARROW_RESOURCE_ROLE_CODES.has(role) && this.keycloakService.isUserInRole(role),
    );
  }

  buildVisibleSections() {
    this.visibleSections = this.allSections.filter((section) =>
      this.isSectionVisible(section),
    );
    this.sidebarPanel.setVisibleSections(this.visibleSections);
    if (this.sidebarPanel.panelOpen && !this.getActiveSection()) {
      this.sidebarPanel.closePanel();
    }
    if (
      this.sidebarPanel.activeSection &&
      !this.visibleSections.some(
        (s) => s.section === this.sidebarPanel.activeSection,
      )
    ) {
      this.sidebarPanel.setActiveSection(null, { closePanel: true });
    }
    this.setActiveSectionFromRoute(this.router.url);
  }

  // ---- Visibility ----

  isSectionVisible(section: MenuSectionType): boolean {
    if (section.conditional && this.companyActivity !== section.conditional) {
      return false;
    }
    if (section.section === "onboarding" && !this.hasOnboardingWork()) {
      return false;
    }
    const visibleItems = section.items.filter((item) => this.isMenuVisible(item));
    if (!visibleItems.length) {
      return false;
    }
    if (
      section.groups?.length &&
      !this.hasUserGroup(section.groups) &&
      !this.isTrialReadOnly
    ) {
      // No group membership at all: only keep the section open if at least
      // one visible item was unlocked by a directly-held narrow resource
      // role (see hasDirectResourceAccess) rather than by group inheritance
      // — an agent scoped to a single resource shouldn't be blanket-hidden
      // by the group gate meant to discriminate un-selected company services.
      return visibleItems.some((item) => this.hasDirectResourceAccess(item));
    }
    return true;
  }

  /**
   * Whether the onboarding checklist has anything actionable left to show. A
   * downgraded (trial-expired) account keeps none of the manage-level authorities
   * every setup step requires, so this is naturally false for it without a
   * separate trial check — same as once every visible step is already done. Either
   * way, the checklist page would otherwise render as an empty greeting with no
   * steps and no "all done" banner (that banner needs at least one required step).
   */
  private hasOnboardingWork(): boolean {
    const required = this.checklist.visibleSetupSteps;
    const optional = this.checklist.visibleOptionalSteps;
    return (
      required.some((step) => !this.checklist.isStepDone(step.id)) ||
      optional.some((step) => !this.checklist.isStepDone(step.id))
    );
  }

  isMenuVisible(menu: MenuTreeType): boolean {
    if (menu.hidden) {
      return false;
    }
    if (menu.hideForActivity && this.companyActivity === menu.hideForActivity) {
      return false;
    }
    if (
      menu.activityCondition &&
      this.companyActivity !== menu.activityCondition
    ) {
      return false;
    }
    // groups and authorities are independent gates — when both are set on the same
    // item, both must pass. Previously this picked only one (whichever was set,
    // preferring authorities), so an item with a broadly-granted baseline role
    // (e.g. REFERENTIAL_MANAGE) but a narrower group requirement (e.g. /MESSAGING)
    // leaked to merchants who never selected that service.
    const groupAllowed =
      !menu.groups?.length ||
      this.hasUserGroup(menu.groups) ||
      this.isTrialReadOnly ||
      this.hasDirectResourceAccess(menu);
    const roleAllowed = !menu.authorities?.length || this.isUserInRole(menu.authorities, menu.roleMode);
    if (!groupAllowed || !roleAllowed) {
      return false;
    }
    if (menu.items?.length) {
      const childVisible = menu.items.some((sub) => this.isSubMenuVisible(sub));
      return childVisible || !!menu.path;
    }
    return true;
  }

  isUserInRole(roles: string[] | undefined, roleMode?: "all" | "any"): boolean {
    if (!roles || roles.length === 0) {
      return true;
    }
    if (roleMode === "any") {
      return roles.some((role) => this.keycloakService.isUserInRole(role));
    }
    if (roleMode === "all" || roles.includes(Role.ADMIN_SOLUTION_UI)) {
      return roles.every((role) => this.keycloakService.isUserInRole(role));
    } else {
      return roles.some((role) => this.keycloakService.isUserInRole(role));
    }
  }

  private canPollUnreadConversations(): boolean {
    return this.isUserInRole([Role.TEAM_INBOX, Role.CONVERSATION_VIEW]);
  }

  // ---- Section / Panel ----

  // Click on rail icon:
  //  - collapsed sidebar → show the flyout menu (2nd click goes to the
  //    section's default page)
  //  - expanded panel → switch section and navigate to its default page
  onRailClick(sectionKey: string, event?: Event) {
    const section = this.visibleSections.find((s) => s.section === sectionKey);
    if (!section) {
      return;
    }

    if (section.directLink) {
      this.closeFlyout();
      // Hide the panel while on a section without a sub-menu, but KEEP the
      // user's pin — their expanded layout must survive a dashboard detour.
      this.sidebarPanel.setActiveSection(sectionKey);
      this.sidebarPanel.closePanel();
      this.router.navigateByUrl(section.directLink);
      this.saveSidebarState();
      return;
    }

    // Pinned but hidden (e.g. coming back from a direct-link section):
    // reopen the expanded panel instead of showing the flyout.
    if (!this.isPanelVisible() && !this.sidebarPanel.panelPinned) {
      if (this.flyoutSection === sectionKey) {
        // Flyout already open (hover or first tap) → go to the default page
        const firstItem = this.resolveDefaultSectionItem(section);
        const targetPath = firstItem
          ? this.resolveMenuTargetPath(firstItem)
          : undefined;
        if (targetPath) {
          this.router.navigateByUrl(targetPath);
        }
        this.closeFlyout();
      } else {
        this.openFlyout(
          sectionKey,
          (event?.currentTarget as HTMLElement) ?? null,
        );
      }
      return;
    }

    // Panel is expanded: keep it visible for the navigation this click triggers.
    this.sidebarPanel.setActiveSection(sectionKey, { openPanel: true });
    this.suppressNextAutoCollapse = true;

    const firstItem = this.resolveDefaultSectionItem(section);
    if (firstItem) {
      this.revealMenu(firstItem.menu);
      const targetPath = this.resolveMenuTargetPath(firstItem);
      if (targetPath) {
        this.router.navigateByUrl(targetPath);
      }
    }
    this.saveSidebarState();
  }

  // ---- Rail flyout (collapsed sidebar) ----

  isPanelVisible(): boolean {
    return this.sidebarPanel.panelOpen && !!this.getActiveSection();
  }

  getFlyoutSection(): MenuSectionType | null {
    if (!this.flyoutSection) {
      return null;
    }
    return (
      this.visibleSections.find((s) => s.section === this.flyoutSection) ?? null
    );
  }

  private openFlyout(sectionKey: string, anchor: HTMLElement | null) {
    const anchorTop = anchor?.getBoundingClientRect().top ?? 72;
    const top = `${Math.max(8, anchorTop - 6)}px`;
    // Anchor next to the clicked icon; the real height is measured after
    // render and the card is only nudged up if it overflows the viewport.
    this.flyoutStyle = this.isRTL
      ? { top, right: "72px" }
      : { top, left: "72px" };
    this.flyoutSection = sectionKey;

    requestAnimationFrame(() => {
      const card = this.el.nativeElement.querySelector(
        ".rail-flyout",
      ) as HTMLElement | null;
      if (!card || this.flyoutSection !== sectionKey) {
        return;
      }
      const maxTop = window.innerHeight - card.offsetHeight - 12;
      const top = Math.max(8, Math.min(anchorTop - 6, maxTop));
      this.flyoutStyle = { ...this.flyoutStyle, top: `${top}px` };
    });
  }

  closeFlyout() {
    this.flyoutSection = null;
  }

  isSubMenuVisible(sub: MenuItemTreeType): boolean {
    if (sub.requiresDisplayGroupAgent && !this.displayGroupAgent) {
      return false;
    }
    if (sub.hideForActivity && this.companyActivity === sub.hideForActivity) {
      return false;
    }
    if (sub.groups?.length && !this.hasUserGroup(sub.groups)) {
      return false;
    }
    return sub.authorities?.length
      ? this.isUserInRole(sub.authorities, sub.roleMode)
      : true;
  }

  onFlyoutNavigate(menu: MenuTreeType | MenuItemTreeType | string | undefined) {
    if (!menu) {
      return;
    }
    this.closeFlyout();
    if (typeof menu === "string") {
      this.router.navigateByUrl(menu);
      return;
    }
    if (!menu.path) {
      return;
    }
    if (menu.queryParams && Object.keys(menu.queryParams).length > 0) {
      this.router.navigate([menu.path], { queryParams: menu.queryParams });
      return;
    }
    this.router.navigateByUrl(menu.path);
  }

  @HostListener("document:keydown.escape")
  onEscapePressed() {
    this.closeFlyout();
    this.closeDrawer();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    if (!this.flyoutSection) {
      return;
    }
    const target = event.target as HTMLElement;
    if (!target.closest(".rail-flyout") && !target.closest(".rail-icon")) {
      this.closeFlyout();
    }
  }

  private resolveDefaultSectionItem(
    section: MenuSectionType,
  ): MenuTreeType | undefined {
    if (section.defaultMenu) {
      const preferred = section.items.find(
        (item) => item.menu === section.defaultMenu && this.isMenuVisible(item),
      );
      if (preferred) {
        return preferred;
      }
    }
    return section.items.find((item) => this.isMenuVisible(item));
  }

  private resolveMenuTargetPath(menu: MenuTreeType): string | undefined {
    if (!menu.path && !menu.items?.length) {
      return undefined;
    }
    if (menu.items?.length) {
      const firstSub =
        menu.items.find((sub) => this.isSubMenuVisible(sub)) ?? menu.items[0];
      return firstSub?.path ?? menu.path;
    }
    return menu.path;
  }

  closePanel() {
    this.sidebarPanel.closeAndUnpin();
    this.saveSidebarState();
  }

  openAndPinPanel() {
    this.sidebarPanel.openAndPin();
    this.saveSidebarState();
  }

  onPanelToggleClick(): void {
    if (!this.canToggleSubPanel()) {
      return;
    }

    if (this.sidebarPanel.panelOpen) {
      this.sidebarPanel.closeAndUnpin();
    } else {
      this.sidebarPanel.openAndPin();
    }
    this.saveSidebarState();
  }

  togglePanel() {
    this.onPanelToggleClick();
  }

  canToggleSubPanel(): boolean {
    return this.sidebarPanel.canToggleSubPanel();
  }

  getActiveSection(): MenuSectionType | null {
    return this.sidebarPanel.getActiveSection();
  }

  isMenuExpanded(menu: MenuTreeType): boolean {
    if (menu.alwaysExpanded || !menu.menu) {
      return true;
    }
    return !this.collapsedMenus.has(menu.menu);
  }

  getPanelTitle(section: MenuSectionType): string {
    // Each section shows a single, stable header — its own label (e.g.
    // "Messaging" over Team Inbox / Contact / Tickets) — rather than morphing
    // into the active item's name.
    return section.label;
  }

  shouldShowPanelHeader(_section: MenuSectionType): boolean {
    return true;
  }

  toggleMenu(menuKey: string | undefined) {
    if (!menuKey) return;
    if (this.isMenuAlwaysExpanded(menuKey)) return;
    if (this.collapsedMenus.has(menuKey)) {
      this.collapsedMenus.delete(menuKey);
    } else {
      this.collapsedMenus.add(menuKey);
    }
    this.saveSidebarState();
  }

  toggleMobileSection(sectionKey: string): void {
    if (this.isMobileSectionExpanded(sectionKey)) {
      this.expandedMobileSections.delete(sectionKey);
      this.collapsedMobileSections.add(sectionKey);
    } else {
      this.collapsedMobileSections.delete(sectionKey);
      this.expandedMobileSections.add(sectionKey);
    }
  }

  isMobileSectionExpanded(sectionKey: string): boolean {
    return this.expandedMobileSections.has(sectionKey) ||
      (this.sidebarPanel.activeSection === sectionKey && !this.collapsedMobileSections.has(sectionKey));
  }

  mobileSectionGrouped(section: MenuSectionType): boolean {
    return !section.directLink;
  }

  onPanelMenuClick(menu: MenuTreeType) {
    if (menu.items?.length) {
      if (menu.alwaysExpanded) {
        return;
      }
      this.toggleMenu(menu.menu);
      return;
    }
    if (menu.path) {
      this.router.navigateByUrl(menu.path);
      this.onNavigate();
    }
  }

  // ---- Route-based active detection ----

  setActiveSectionFromRoute(url: string) {
    const normalizedUrl = url.replace(/^#/, "");
    const path = normalizedUrl.split("?")[0];
    const currentQuery = this.getQueryParamsFromUrl(normalizedUrl);
    type RouteMatch = {
      section: string;
      menu: string | null;
      length: number;
      queryScore: number;
    };
    const matches: RouteMatch[] = [];

    const consider = (
      sectionKey: string,
      matchPath: string,
      menuKey?: string | null,
      itemQuery?: { [key: string]: string },
    ) => {
      const exact = path === matchPath;
      const prefix = path.startsWith(matchPath + "/");
      if (!exact && !prefix) {
        return;
      }

      let queryScore = 0;
      if (itemQuery && Object.keys(itemQuery).length > 0) {
        const queryMatches = Object.entries(itemQuery).every(
          ([key, value]) => currentQuery[key] === value,
        );
        if (!queryMatches) {
          return;
        }
        queryScore = 1;
      } else if (
        path === APP_PATHS.catalog.products &&
        (currentQuery["itemType"] === "ROOM" ||
          currentQuery["itemType"] === "EXTRA")
      ) {
        return;
      }

      matches.push({
        section: sectionKey,
        menu: menuKey ?? null,
        length: matchPath.length,
        queryScore,
      });
    };

    for (const section of this.visibleSections) {
      if (section.directLink) {
        consider(section.section, section.directLink);
      }
      for (const item of section.items) {
        // Hidden groups (activity/role filtered) must not steal the route
        // match from the visible group that shares the same path.
        if (!this.isMenuVisible(item)) {
          continue;
        }
        if (item.path) {
          consider(
            section.section,
            item.path,
            item.menu ?? null,
            item.queryParams,
          );
        }
        if (item.items) {
          for (const sub of item.items) {
            if (!this.isSubMenuVisible(sub)) {
              continue;
            }
            consider(
              section.section,
              sub.path,
              item.menu ?? null,
              sub.queryParams,
            );
            for (const path of sub.alsoActiveOn ?? []) {
              consider(section.section, path, item.menu ?? null);
            }
          }
        }
      }
    }

    if (matches.length === 0) {
      return;
    }

    matches.sort((a, b) => {
      if (b.queryScore !== a.queryScore) {
        return b.queryScore - a.queryScore;
      }
      if (b.length !== a.length) {
        return b.length - a.length;
      }
      if (a.section === "onboarding" && b.section !== "onboarding") {
        return 1;
      }
      if (b.section === "onboarding" && a.section !== "onboarding") {
        return -1;
      }
      return 0;
    });

    this.sidebarPanel.setActiveSection(matches[0].section);
    this.collapsedMobileSections.delete(matches[0].section);
    this.sidebarPanel.updatePageFromRoute(path, matches[0]);
    this.revealMenu(matches[0].menu);
    this.saveSidebarState();
  }

  private getQueryParamsFromUrl(url: string): Record<string, string> {
    const queryIndex = url.indexOf("?");
    if (queryIndex === -1) {
      return {};
    }
    const params: Record<string, string> = {};
    const search = url.slice(queryIndex + 1);
    for (const part of search.split("&")) {
      if (!part) {
        continue;
      }
      const [key, value = ""] = part.split("=");
      if (key) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    }
    return params;
  }

  private getCurrentQueryParams(): Record<string, string> {
    return this.getQueryParamsFromUrl(this.router.url.replace(/^#/, ""));
  }

  isItemActive(path: string | undefined): boolean {
    if (!path) return false;
    const currentPath = this.router.url.replace(/^#/, "").split("?")[0];
    return currentPath === path || currentPath.startsWith(path + "/");
  }

  isItemExactActive(
    path: string,
    queryParams?: { [key: string]: string },
  ): boolean {
    const currentUrl = this.router.url.replace(/^#/, "");
    const currentPath = currentUrl.split("?")[0];
    if (currentPath !== path) {
      return false;
    }
    if (!queryParams || Object.keys(queryParams).length === 0) {
      const currentQuery = this.getCurrentQueryParams();
      return !currentQuery["itemType"];
    }
    const currentQuery = this.getCurrentQueryParams();
    return Object.entries(queryParams).every(
      ([key, value]) => currentQuery[key] === value,
    );
  }

  isSubItemActive(sub: MenuItemTreeType): boolean {
    if (sub.alsoActiveOn?.some((path) => this.isItemActive(path))) {
      return true;
    }
    return this.isItemExactActive(sub.path!, sub.queryParams);
  }

  isMenuItemActive(menu: MenuTreeType | MenuItemTreeType): boolean {
    if (!menu.path) {
      return false;
    }
    if (this.isWorkflowMenuActive(menu)) {
      return true;
    }
    if (menu.queryParams && Object.keys(menu.queryParams).length > 0) {
      return this.isItemExactActive(menu.path, menu.queryParams);
    }
    if (this.isShadowedByQueryItem(menu.path)) {
      return false;
    }
    if (menu.path === APP_PATHS.catalog.products) {
      return this.isItemExactActive(menu.path);
    }
    return this.isItemActive(menu.path);
  }

  /**
   * Another visible line opens the same page with query params and is the one open now (restaurant "Livraison"
   * is Commandes ?panel=delivery): only that line is highlighted.
   */
  private isShadowedByQueryItem(path: string): boolean {
    return this.visibleSections.some((section) =>
      section.items.some(
        (item) =>
          item.path === path &&
          !!item.queryParams &&
          Object.keys(item.queryParams).length > 0 &&
          this.isMenuVisible(item) &&
          this.isItemExactActive(path, item.queryParams),
      ),
    );
  }

  /** Templates, flux bots, and AI agents share workflow routes — keep sidebar highlight in sync. */
  private isWorkflowMenuActive(menu: MenuTreeType | MenuItemTreeType): boolean {
    const menuKey = "menu" in menu ? menu.menu : undefined;
    if (!menuKey) {
      return false;
    }

    const prefixes: Record<string, string[]> = {
      templates: [
        APP_PATHS.workflow.templates,
        APP_PATHS.workflow.forms,
        APP_PATHS.workflow.automationFlows,
      ],
      "workflow-list": [APP_PATHS.workflow.bots],
      "ai-agents": [APP_PATHS.workflow.aiAgents, APP_PATHS.workflow.aiAssistance],
    };

    const paths = prefixes[menuKey];
    if (!paths?.length) {
      return false;
    }

    const currentPath = this.router.url.replace(/^#/, "").split("?")[0];
    return paths.some(
      (path) => currentPath === path || currentPath.startsWith(`${path}/`),
    );
  }

  isMenuSectionActive(menu: MenuTreeType): boolean {
    if (!menu.items) return this.isItemActive(menu.path);
    return menu.items.some((sub) => this.isSubItemActive(sub));
  }

  private isMenuAlwaysExpanded(menuKey: string): boolean {
    for (const section of this.visibleSections) {
      const menu = section.items.find((item) => item.menu === menuKey);
      if (menu?.alwaysExpanded) {
        return true;
      }
    }
    return false;
  }

  // ---- Navigation ----

  onNavigate() {
    this.saveSidebarState();
  }

  // Collapse the detail panel after any route change unless the user pinned it
  // open with the expand button.
  private autoCollapseAfterNavigation() {
    if (this.suppressNextAutoCollapse) {
      this.suppressNextAutoCollapse = false;
      return;
    }
    if (this.sidebarPanel.panelOpen && !this.sidebarPanel.panelPinned) {
      this.sidebarPanel.closePanel();
      this.saveSidebarState();
    }
  }

  // ---- Persistence ----

  /** The group owning the current route is never left folded. */
  private revealMenu(menuKey: string | null | undefined) {
    if (menuKey) {
      this.collapsedMenus.delete(menuKey);
    }
  }

  saveSidebarState() {
    this.sidebarPanel.persist([...this.collapsedMenus]);
  }

  restoreSidebarState() {
    this.collapsedMenus = new Set(this.sidebarPanel.restore());
  }

  // ---- Drawer (mobile) ----

  closeDrawer(): void {
    const wasOpen = this.sidebarPanel.mobileOpen();
    this.sidebarPanel.closeMobile();
    if (wasOpen) {
      this.el.nativeElement.ownerDocument.querySelector('app-header button[aria-controls="drawer-navigation"]')?.focus();
    }
  }

  @HostListener("window:resize")
  closeMobileOnDesktop(): void {
    if (window.innerWidth >= 640 && this.sidebarPanel.mobileOpen()) this.closeDrawer();
  }

  signout() {
    this.authService.logout();
  }

  // ---- RTL ----

  isRTL: boolean = false;

  get railTooltipPosition(): "left" | "right" {
    return this.isRTL ? "left" : "right";
  }

  checkLanguageDirection(lang?: string) {
    const currentLang = lang || sessionStorage.getItem("lang") || "fr";
    this.isRTL = currentLang === "ar";
    if (this.isRTL) {
      this.renderer.setAttribute(this.el.nativeElement, "dir", "rtl");
      this.renderer.addClass(this.el.nativeElement, "rtl");
    } else {
      this.renderer.setAttribute(this.el.nativeElement, "dir", "ltr");
      this.renderer.removeClass(this.el.nativeElement, "rtl");
    }
  }
}
